import React from 'react'
import { InputLabel, Link, Stack, styled, useMediaQuery, useTheme } from '@mui/material'
import Typography from '../../../../components/Typography'
import { useCampaignContext } from './CampaignContext'
import { AccordionItemSummary, getTotalUnits, SalesAccordionItem } from './QuantityTab'
import {
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from '../../../azure-b2c/storage'
import { useSelector } from 'react-redux'
import {
  BoldWrapper,
  OrderLocationButton,
  PadButton,
  PadContent,
  PadIcon,
  WrapperA,
} from '../../../../components/Pad/Pad'
import Input, { InputProps } from '../../../../components/Input'
import { FormikAgreementForm, SaleAgreementPromo, SaleAgreementPromoProduct } from './types'
import Checkbox from '../../../../components/Checkbox'
import { ModalActions } from '../../../../components/Modal'
import { Button } from '../../../../components/Button'
import { Form, Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { nzPhoneNumberRegex } from '../../../../util/validatePhoneNumber'
import TextField from '../../../../components/TextField'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import _ from 'lodash'
import { splitAddress } from '../../../../services/als/splitAddress'
import { addGST, getGST } from '../../../../util/addGst'
import { formatPrice } from '../../../../util/helpers'
import { OrderSummary } from '../../../../components/OrderSummary'
import TestIds from '../../../../testIds'
import { submitCampaignApi } from './api'
import { Loader } from '../../../../components/Loader'
import { useNavigate } from 'react-router-dom'
import { colors } from '../../../../themes/colors'
import { calculateTotalFreightCost } from './utils'
import TabHeader from './TabHeader'
import { nameRegex } from '../../../../util/validateName'

const calculateTotalPrice = (products: SaleAgreementPromoProduct[]) => {
  return products.reduce((total, product) => {
    if (!product.orders) return 0
    const totalOrders = product.orders.reduce((sum, val) => sum + val.quantity, 0)
    return total + totalOrders * product.unitPrice
  }, 0)
}

const StyledSplitPaymentInnerContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}))

const StyledSplitPaymentContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '',
})<{ checked?: boolean }>(({ theme, checked }) => ({
  background: checked ? theme.colors.primary100 : 'transparent',
  borderRadius: checked ? '8px' : '0',
  gap: '4px',
  padding: '8px',
}))

const StyledSummaryContainer = styled(Stack)(({ theme }) => ({
  background: theme.colors.primary50,
  border: `1px solid ${theme.colors.primary200}`,
}))

function hasAnyNonNullishField<T extends Record<string, unknown>>(obj: T): boolean {
  return Object.values(obj).some((value) => value != null)
}

export interface ReviewTabProps {
  config: SaleAgreementPromo
}
export const ReviewTab: React.FC<ReviewTabProps> = ({ config }) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const { state } = useCampaignContext()
  const isFreightInclusive = !config.freightRequired
  const [submitting, setSubmitting] = React.useState(false)
  const [apiError, setApiError] = React.useState<string>()
  const navigate = useNavigate()

  const totalFreightCost = isFreightInclusive
    ? 0
    : calculateTotalFreightCost(state.productSelected, state.freightCharge)
  const totalPrice = calculateTotalPrice(state.productSelected)

  const accountEmail = useSelector(selectEmailForPrefill)
  const accountUsername = useSelector(selectNameForPrefill)
  const accountPhone = useSelector(selectPhoneForPrefill)

  const dairySupplyNum =
    state.addressSelected && 'dairySupplyNumber' in state.addressSelected
      ? (state.addressSelected.dairySupplyNumber as string)
      : ''

  const isSm = useMediaQuery(useTheme().breakpoints.down('sm'))

  const initialValues = React.useMemo(() => {
    return {
      accName: accountUsername ?? '',
      accEmail: accountEmail ?? '',
      accPhoneNumber: accountPhone ?? '',
      productsSelected: state.productSelected,
      splitPayment: false,
      reference: '',
      splitPaymentAccName: '',
      splitPaymentAccNumber: '',
      splitPaymentContribution: 0,
      tcAccepted: false,
      deliveryContactName: '',
      deliveryPhoneNum: '',
      deliverySupplyNum: dairySupplyNum,
      deliveryDetails: '',
      hiabTruckRequired: false,
    }
  }, [accountUsername, accountEmail, accountPhone, state.productSelected, dairySupplyNum])

  return (
    <Formik<FormikAgreementForm>
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        accName: Yup.string().required('Please enter a name.'),
        accEmail: Yup.string().email('Invalid Email Format.').required('Please enter an email.'),
        accPhoneNumber: Yup.string()
          .trim()
          .matches(nzPhoneNumberRegex, 'Invalid Phone Number Format.')
          .required('Please enter a phone number.'),
        reference: Yup.string().max(60, 'Maximum 60 characters allowed.').optional(),
        splitPayment: Yup.boolean(),
        splitPaymentAccName: Yup.string().when('splitPayment', {
          is: true,
          then: (schema) => schema.required('Required'),
          otherwise: (schema) => schema.optional(),
        }),
        splitPaymentAccNumber: Yup.string().when('splitPayment', {
          is: true,
          then: (schema) =>
            schema
              .required('Required')
              .matches(/^[0-9]*$/, 'Number Format only.')
              .min(9, 'Must be 9 digit number.')
              .max(9, 'Must be 9 digit number'),
          otherwise: (schema) => schema.optional(),
        }),
        splitPaymentContribution: Yup.number().when('splitPayment', {
          is: true,
          then: (schema) => schema.required('Required').min(1, 'Minimum 1% or more'),
          otherwise: (schema) => schema.optional(),
        }),
        tcAccepted: account?.hasTfoAccess ? Yup.boolean().optional() : Yup.boolean().isTrue(),
        deliveryContactName: Yup.string()
          .matches(nameRegex, 'Invalid Name Format. (alphabet only)')
          .required('Delivery contact name is required'),
        deliveryPhoneNum: Yup.string()
          .trim()
          .matches(nzPhoneNumberRegex, 'Invalid Phone Number Format.')
          .required('Please enter a phone number.'),
        deliverySupplyNum: Yup.string(),
        deliveryDetails: Yup.string().optional(),
        hiabTruckRequired: Yup.boolean().optional(),
      })}
      onSubmit={(value) => {
        if (account) {
          setSubmitting(true)
          submitCampaignApi(value, state, account, config.salesAgreementId)
            .then(() => {
              navigate('/agreements/campaign-success', {
                state: {
                  campaignTitle: config?.displayTitle ?? config?.documentTitle,
                },
              })
            })
            .catch((error: string) => {
              setApiError(
                'We’re sorry, but an unexpected error has occurred. We are unable to submit this agreement at this time.',
              )
              console.log(error)
            })
            .finally(() => {
              setSubmitting(false)
            })
        }
      }}
    >
      {({ setFieldValue, errors, submitForm }) => (
        <Form key={'campaign-submission'} style={{ height: '100%' }}>
          <Stack height='100%' margin='auto' display={submitting ? 'flex' : 'none'}>
            <Loader title='Submitting' />
          </Stack>
          <Stack
            direction='column'
            gap={'1rem'}
            pb='50px'
            display={submitting ? 'none' : undefined}
          >
            <TabHeader title='Summary' caption='Review your products and confirm agreement' />
            <ProductsSelected isFreightInclusive={isFreightInclusive} />
            <StyledSummaryContainer p='1rem' direction='column'>
              <Stack direction={'column'} gap={'1rem'}>
                <OrderSummary
                  showDivider
                  accountName={account?.accountName}
                  accountNumber={account?.accountNumber}
                  gst={formatPrice(getGST(totalPrice + totalFreightCost))}
                  title='Order Summary'
                  total={formatPrice(addGST(totalPrice + totalFreightCost))}
                  delivery={formatPrice(addGST(totalFreightCost))}
                  deliveryLabel={
                    isFreightInclusive ? (
                      <Typography variant='body2'>
                        Freight{' '}
                        <Typography
                          color={'grey700'}
                          fontSize='12px'
                          component='span'
                          lineHeight={'18px'}
                        >
                          (inclusive)
                        </Typography>
                      </Typography>
                    ) : (
                      <Typography variant='body2'>
                        Freight{' '}
                        <Typography fontSize='12px' component='span' lineHeight={'18px'}>
                          (GST incl.)
                        </Typography>
                      </Typography>
                    )
                  }
                />

                <DeliveryAddressSelected />
                <DeliveryInfoForm />
                <AccountHolder />

                <Stack>
                  <SplitPaymentOption />
                  {!account?.hasTfoAccess ? (
                    <StyledSplitPaymentContainer>
                      <Checkbox
                        label={
                          <Typography variant='body2' fontWeight={400}>
                            I accepted the{' '}
                            <Link target='_blank' href={config.termsConditionsUrl}>
                              terms and conditions
                            </Link>{' '}
                            for these products
                          </Typography>
                        }
                        onChange={(_, checked) => setFieldValue('tcAccepted', checked)}
                      />
                      {errors.tcAccepted && (
                        <Typography variant='body2' color='error500'>
                          Terms and Conditions must be accepted
                        </Typography>
                      )}
                    </StyledSplitPaymentContainer>
                  ) : (
                    <Typography variant='body2' color='grey800'>
                      Customer must accept the terms and conditions via email
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </StyledSummaryContainer>
          </Stack>
          <ModalActions showDivider>
            <Stack width='100%' direction={isSm ? 'column' : 'row'}>
              <Stack width='100%'>
                {!hasAnyNonNullishField(errors) && apiError && (
                  <Typography marginRight='auto' variant='body1' color='error500' mb={2}>
                    {apiError}
                  </Typography>
                )}

                {hasAnyNonNullishField(errors) && (
                  <Typography marginRight='auto' variant='body1' color='error500' mb={2}>
                    Oops! Some information is missing.
                  </Typography>
                )}
              </Stack>
              <Button
                type='submit'
                onSubmit={submitForm}
                onClick={submitForm}
                key='next-step-btn'
                variant='contained'
                data-testid={TestIds.feat.campaign.reviewTab.submitButton}
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                })}
              >
                Submit
              </Button>
            </Stack>
          </ModalActions>
        </Form>
      )}
    </Formik>
  )
}

interface FormikInputProps
  extends Pick<
    InputProps,
    | 'label'
    | 'testId'
    | 'type'
    | 'multiline'
    | 'placeholder'
    | 'required'
    | 'helperText'
    | 'helperTextColor'
    | 'disableError'
    | 'disabled'
    | 'inputProps'
    | 'minRows'
  > {
  formKey: keyof FormikAgreementForm
}

const FormikInput: React.FC<FormikInputProps> = ({ formKey, ...restProps }) => {
  const ctx = useFormikContext<FormikAgreementForm>()
  const theme = useTheme()
  if (typeof ctx.values[formKey] !== 'string') return
  const error = ctx.errors[formKey] as string | undefined

  return (
    <Input
      onChange={(e) => {
        ctx.setFieldValue(formKey, e.target.value)
      }}
      value={ctx.values[formKey]}
      error={Boolean(error)}
      errorMessage={error}
      helperTextColor={theme.colors.grey700}
      {...restProps}
    />
  )
}

const FormikInputPercent: React.FC<FormikInputProps> = ({ formKey, type, testId, label }) => {
  const ctx = useFormikContext<FormikAgreementForm>()
  const theme = useTheme()
  const error = ctx.errors[formKey] as string | undefined

  return (
    <TextField
      sx={{ padding: '10.5px 8px' }}
      label={label}
      type={type}
      onChange={(e) => {
        const val = e.target.value

        if (val.match(/[^0-9]/)) {
          return e.preventDefault()
        }

        if (Number(val) > 100) return

        ctx.setFieldValue(formKey, _.trimStart(String(val), '0'))
      }}
      value={String(ctx.values[formKey])}
      variant={'outlined'}
      testId={testId}
      inputProps={{
        inputMode: 'numeric',
      }}
      error={Boolean(error)}
      helperText={typeof ctx.errors[formKey] === 'string' ? ctx.errors[formKey] : ' '}
      helperTextColor={theme.colors.error700}
      InputProps={{
        endAdornment: <MaterialSymbol color={theme.colors.primary500}>percent</MaterialSymbol>,
      }}
    />
  )
}

interface ProductsSelectedProps {
  isFreightInclusive: boolean
}

const ProductsSelected = ({ isFreightInclusive }: ProductsSelectedProps) => {
  const { state } = useCampaignContext()

  return state.productSelected.map((prod) => {
    const summary: AccordionItemSummary[] = [
      {
        type: 'unit',
        title: prod.unit,
        calculation: prod.orders ? prod.orders.reduce((a, b) => a + b.quantity, 0) : 0,
      },
      {
        type: 'price',
        title: 'Total',
        calculation: prod.unitPrice * getTotalUnits(state.productSelected, prod.itemNumber),
      },
    ]

    if (!isFreightInclusive) {
      summary.push({
        type: 'price',
        title: 'Freight',
        calculation: calculateTotalFreightCost(prod, state.freightCharge),
      })
    }

    return (
      <SalesAccordionItem
        key={prod.itemNumber}
        productId={prod.itemNumber}
        unit={prod.unit}
        minQuantity={prod.quantity}
        title={prod.description}
        editable={false}
        defaultExpanded={true}
        summary={summary}
      />
    )
  })
}

const DeliveryAddressSelected = () => {
  const { state, setState } = useCampaignContext()
  const handleOpenPickUpStoreModal = () => {
    setState({ tabIndex: 0 })
  }

  if (state.addressSelected) {
    const address = state.addressSelected
    let subtitle = ''
    if ('UniqueId' in address) {
      const { first, second } = splitAddress(address.FullAddress)
      subtitle = `${first}, ${second}`
    } else {
      subtitle = `${address.street.replace('\n', ', ')} ${address.city}, ${address.zipCode}`
    }
    return (
      <OrderLocationButton
        shade='light'
        onClick={handleOpenPickUpStoreModal}
        buttonTestId={TestIds.feat.campaign.reviewTab.orderLocationButton}
        title='Delivery'
        subtitle={
          <>
            {subtitle.split('\n').map((e) => {
              return (
                <span key={e}>
                  {e}
                  <br />
                </span>
              )
            })}
          </>
        }
        isEditEnabled
      />
    )
  }
  return (
    <PadButton
      shade='light'
      sx={{ justifyContent: 'flex-start', p: 1 }}
      onClick={handleOpenPickUpStoreModal}
      data-testid={TestIds.feat.campaign.reviewTab.pickupOrDelivery}
    >
      <WrapperA>
        <BoldWrapper>
          <PadIcon className='material-symbols-outlined'>add</PadIcon>
          <PadContent>Select pickup store or delivery</PadContent>
        </BoldWrapper>
      </WrapperA>
    </PadButton>
  )
}

const DeliveryInfoForm = () => {
  return (
    <Stack gap={1}>
      <FormikInput
        label='Delivery contact name'
        formKey='deliveryContactName'
        placeholder='Enter a delivery contact name here...'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.contactNameInput}
        required
        type='text'
      />

      <FormikInput
        label='Delivery phone number'
        formKey='deliveryPhoneNum'
        placeholder='Enter a delivery phone number...'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.phoneNumberInput}
        required
        type='tel'
      />

      <FormikInput
        type='text'
        label='Dairy supply number'
        formKey='deliverySupplyNum'
        placeholder='Include your Dairy supply number, if you have one.'
        inputProps={{ maxLength: 60 }}
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.supplyNumberInput}
        // helperText=''
        // helperTextColor={colors.grey700}
      />

      <FormikInput
        type='text'
        multiline
        minRows={4}
        placeholder='Enter additional delivery details here...'
        formKey='deliveryDetails'
        label='Delivery Details'
        testId={TestIds.feat.campaign.reviewTab.deliveryInfo.detailsInput}
      />
    </Stack>
  )
}

const AccountHolder = () => {
  const account = useSelector(selectCurrentFarmerAccount)
  const accountEmail = useSelector(selectEmailForPrefill)
  const accountUsername = useSelector(selectNameForPrefill)
  const { values } = useFormikContext<FormikAgreementForm>()

  const confirmTitle = 'Account holder / authoriser'
  const nameLabel = account?.hasTfoAccess ? 'Customer name' : 'Name'
  const phoneLabel = account?.hasTfoAccess ? 'Customer phone number' : 'Phone number'
  const emailLabel = account?.hasTfoAccess ? 'Customer email address' : 'Email address'
  const refLabel = account?.hasTfoAccess ? 'Customer reference' : 'Your reference'

  return (
    <Stack direction={'column'} py='0.5rem' gap={2}>
      <Typography variant='h6' fontWeight={700} fontSize={'20px'}>
        {confirmTitle}
      </Typography>
      <Typography variant='body2'>Please confirm your contact details</Typography>

      {account?.hasTfoAccess && (
        <Stack width='auto' borderLeft={`4px solid ${colors.grey900}`} pl={'8px'} gap={'2px'}>
          <Typography variant='body2' fontWeight={'bold'}>
            {account?.accountName}
          </Typography>
          <Typography variant='body2'>{account.accountNumber}</Typography>
        </Stack>
      )}
      <Stack>
        {!account?.hasTfoAccess && accountUsername !== undefined ? (
          <Stack gap={0.2} mb={2} borderLeft={`4px solid ${colors.grey900}`} pl={2}>
            <Typography variant='body2' fontWeight={'bold'}>
              {account?.accountName}
            </Typography>
            <Typography variant='body2'>{account?.accountNumber}</Typography>
            <Typography variant='body2'>{accountUsername}</Typography>
            <Typography variant='body2'>{accountEmail}</Typography>
          </Stack>
        ) : (
          <>
            <FormikInput
              type='text'
              label={nameLabel}
              testId={TestIds.feat.campaign.reviewTab.nameInput}
              required
              formKey='accName'
            />
            <FormikInput
              type='text'
              label={emailLabel}
              testId={TestIds.feat.campaign.reviewTab.emailInput}
              required
              formKey='accEmail'
            />
          </>
        )}

        <FormikInput
          type='tel'
          label={phoneLabel}
          testId={TestIds.feat.campaign.reviewTab.phoneInput}
          required
          formKey='accPhoneNumber'
        />
        <FormikInput
          type='text'
          label={refLabel}
          testId={TestIds.feat.campaign.reviewTab.refInput}
          formKey='reference'
          helperText={`${values.reference.length}/60 characters`}
          inputProps={{ maxLength: 60 }}
          disableError
        />
      </Stack>
    </Stack>
  )
}

const SplitPaymentOption = () => {
  const { values, setFieldValue } = useFormikContext<FormikAgreementForm>()
  return (
    <StyledSplitPaymentContainer checked={values.splitPayment}>
      <Checkbox
        label={
          <Typography variant='body2' fontWeight={400}>
            Split payment with another farm
          </Typography>
        }
        onChange={(_, checked) => setFieldValue('splitPayment', checked)}
      />
      {values.splitPayment && (
        <StyledSplitPaymentInnerContainer>
          <FormikInput
            type='text'
            formKey='splitPaymentAccName'
            label='Account name'
            placeholder=''
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNameInput}
          />
          <FormikInput
            type='text'
            formKey='splitPaymentAccNumber'
            label='Account number'
            placeholder=''
            testId={TestIds.feat.campaign.reviewTab.splitPay.accNumInput}
            inputProps={{ maxLength: 9 }}
          />
          <Stack gap='24px' direction={'row'} alignItems={'flex-start'}>
            <Stack width='30%' minWidth='140px'>
              <FormikInputPercent
                formKey='splitPaymentContribution'
                label='Their contribution'
                placeholder=''
                testId={TestIds.feat.campaign.reviewTab.splitPay.splitInput}
                type='number'
              />
            </Stack>
            <Stack alignSelf='center'>
              <Typography>-</Typography>
            </Stack>
            <Stack>
              <InputLabel>My contribution</InputLabel>
              <Stack direction='row' height='48px'>
                <Typography
                  alignSelf={'center'}
                  data-testid={TestIds.feat.campaign.reviewTab.splitPay.splitResult}
                >
                  {`${100 - values.splitPaymentContribution}%`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </StyledSplitPaymentInnerContainer>
      )}
    </StyledSplitPaymentContainer>
  )
}
